import { useEffect, useState } from "react"

import { ChevronDown, Lock, Scale } from "lucide-react"

import { ActionButton } from "@/components/buttons/ActionButton"
import { Avatar, AvatarFallback } from "@ui/avatar"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"
import { Separator } from "@ui/separator"
import { getFirstName, getInitials } from "@/lib/users"
import { useUserSession } from "@session/UserSession"

export function UserButton() {
    const { authToken, logout, client } = useUserSession()
    const [open, setOpen] = useState(false)
    const [pictureUrl, setPictureUrl] = useState<string | null>(null)

    const handleNavigateToPrivacy = () => {
        window.open("https://vertesiahq.com/privacy", "_blank")
    }

    const handleNavigateToTerms = () => {
        window.open("https://vertesiahq.com/terms", "_blank")
    }

    const handleLogout = () => {
        logout()
    }

    useEffect(() => {
        if (!client || !authToken) {
            setPictureUrl(null)
            return
        }

        client.users.retrieve(authToken.sub).then((response) => {
            setPictureUrl(response?.picture ? response.picture : null)
        })
    }, [authToken, client])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <div className='flex flex-row border border-gray-300 items-center justify-center cursor-pointer hover:bg-gray-200 rounded-md'>
                    <div className='px-2 text-sm font-light text-left'>
                        {getFirstName(authToken?.name)}
                    </div>
                    <div className='border-l border-gray-300'>
                        <ChevronDown className={`p-1 opacity-50 transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"}`} />
                    </div>
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-[350px] mb-2 mr-4 text-primary-dark p-4 border shadow" align="start">
                <div className="flex flex-col space-y-2">
                    <div className="flex items-start space-x-2">
                        <Avatar className="h-12 w-12 bg-primary-dark">
                            <AvatarFallback className="text-white text-xl hover:opacity-75">
                                {
                                    pictureUrl
                                        ? <img src={pictureUrl} alt="User Avatar" />
                                        : getInitials(authToken?.name)
                                }
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                            <span className="font-bold">{authToken?.name}</span>
                            <span className="text-sm italic">{authToken?.email}</span>
                        </div>
                    </div>

                    <Separator className='bg-gray-300' />

                    <div className="flex items-center justify-between">
                        <span>Terms of Service</span>
                        <ActionButton icon={<Scale />} size="icon" variant="ghost" onClick={handleNavigateToTerms} />
                    </div>

                    <div className="flex items-center justify-between">
                        <span>Privacy</span>
                        <ActionButton icon={<Lock />} size="icon" variant="ghost" onClick={handleNavigateToPrivacy} />
                    </div>

                    <Separator className='bg-gray-300' />

                    <ActionButton icon="Sign Out" variant="outline" onClick={handleLogout} />
                </div>
            </PopoverContent>
        </Popover>
    )
}